import React, { useLayoutEffect, useState } from 'react';
import './ManageRenewalsPage.scss';
import DocumentTitle from '../../shared/components/DocumentTitle';
import { ContainerInner } from '../../shared/common/ContainerInner';
import { LoadingWrapper } from '../../shared/components/LoadingWrapper';
import LoadingOverlay from '../../shared/components/LoadingOverlay';
import { useManageRenewalsTotals } from '../../api/subscription-management/renewals/useManageRenewalsTotals';
import { usePaginationAndSortParams } from '../../shared/hooks/usePaginationAndSortParams';
import { ManageRenewalsHeader } from './ManageRenewalsHeader';
import { updateRenewals } from '../../api/subscription-management/renewals/updateRenewals';
import { useManageRenewalsData } from './useManageRenewalsData';
import { ManageRenewalTableHeader } from './table/ManageRenewalTableHeader';
import { ManageRenewalTableBody } from './table/ManageRenewalTableBody';
import { useSelector } from 'react-redux';
import { AppState } from '../../store/appState';
import { useRenewalLineUpdateMutation } from '../../api/subscription-management/renewals/useRenewalLineUpdateMutation';
import { SelectionContextProvider } from './context/SelectionContext';
import {
    useEditAllRenewalType,
    useEditRenewalType
} from '../../api/subscription-management/renewals/useBulkChangeRenewalType';
import { ShowAllContextProvider } from '../../shared/subscription-management/ShowAllContext';
import Pagination from '../../shared/common/Pagination';
import { useTranslation } from 'react-i18next';

export const ManageRenewalsPage = () => {
    const { t } = useTranslation();
    const [updateRenewalsState, setUpdateRenewalsState] = useState<'loading' | 'success' | 'error'>('loading');
    const spocCurrency = useSelector((state: AppState) => state.authentication.spoc.currency);

    const { mutate: updateRenewalLine, isLoading: isLoadingRenewalLineUpdate } = useRenewalLineUpdateMutation();

    useLayoutEffect(() => {
        updateRenewals()
            .then(() => {
                setUpdateRenewalsState('success');
            })
            .catch(() => {
                setUpdateRenewalsState('error');
            });
    }, []);

    const { page, pageSize, setPage } = usePaginationAndSortParams({ defaultPageSize: 50 });

    const { data: totals, isLoading: isLoadingTotals } = useManageRenewalsTotals({
        enabled: updateRenewalsState === 'success'
    });

    const { isLoading, renewalLine, tableHeader, tableBody } = useManageRenewalsData({
        page,
        pageSize,
        updateRenewalsState,
        spocCurrency,
        updateRenewalLine
    });

    const { mutate: editAllRenewalTypeMutation, isLoading: isLoadingEditAllRenewalType } = useEditAllRenewalType();
    const { mutate: editRenewalTypeMutation, isLoading: isLoadingEditRenewalType } = useEditRenewalType();

    const productIds: string[] = tableBody?.list.map(line => line.product.id) ?? [];

    const loading =
        updateRenewalsState === 'loading' ||
        isLoading ||
        isLoadingTotals ||
        isLoadingRenewalLineUpdate ||
        isLoadingEditAllRenewalType ||
        isLoadingEditRenewalType;

    return (
        <DocumentTitle title={t('components.documentTitle.subscriptionManagement')}>
            {loading ? (
                <ContainerInner>
                    <LoadingWrapper>
                        <LoadingOverlay loading={loading} />
                    </LoadingWrapper>
                </ContainerInner>
            ) : null}
            <div className="manage-subscription-renewals-page">
                <SelectionContextProvider>
                    <ManageRenewalsHeader
                        totals={totals}
                        onPageChange={setPage}
                        page={page}
                        pageSize={pageSize}
                        renewalLines={renewalLine}
                        productIds={productIds}
                        editAllRenewalTypeMutation={editAllRenewalTypeMutation}
                        editRenewalTypeMutation={editRenewalTypeMutation}
                    />

                    <table className="manage-renewals-table">
                        {tableHeader && tableBody ? (
                            <ShowAllContextProvider>
                                <ManageRenewalTableHeader {...tableHeader} />
                                <ManageRenewalTableBody {...tableBody} />
                            </ShowAllContextProvider>
                        ) : null}
                    </table>
                </SelectionContextProvider>

                <Pagination pageSize={pageSize} currentPage={page} onPageChange={setPage} totalItems={renewalLine} />
            </div>
        </DocumentTitle>
    );
};
